import {updateParameters} from '../../../api/postprocessor/UpdateParameters';
import {
  OnnxTestReport,
  postTestOnnxReport,
  TestInitiateRequest,
} from '../../../api/test/TestInitiate';
import {
  setONNXTask,
  resetAllTasks,
  resetInferenceTask,
  TASK_STATUS,
  useDeploy,
} from '../useDeploy';
import {useDeployTask} from './useDeployTask';
import {useInferenceTask} from './useInferenceTask';
import {useVideoStreamingTask} from './useVideoStreamingTask';
import {useTextTask} from './useTextTask';
import {useSelector} from 'react-redux';
import {getServerConfiguration} from '../../../store/config/selectors';
import {useHlsStreamingTask} from './useHlsStreamingTask';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../HlsVideoStream';

type DeployConfigState = ReturnType<typeof useDeploy>[0]['deployConfig'];

export const useTaskManager = (projectId: string) => {
  const [
    {onnxTask, streamingTask, streamingDevices, chatConnection},
    dispatch,
  ] = useDeploy();
  const {runDeploy, terminateDeploy} = useDeployTask(projectId);
  const [runInference] = useInferenceTask(projectId);
  const [runStreamingTask, stop] = useVideoStreamingTask(projectId);
  const [runTestInference] = useHlsStreamingTask(projectId);
  const serverConfig = useSelector(getServerConfiguration);
  const [runTextTask, stopTextTask] = useTextTask(projectId);

  class GspTaskStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.STREAM_FINISHED:
          handleCancel(undefined, event.subject);
          break;
        case EVENT_TYPE.ERROR:
          handleCancel(undefined, event.subject);
          break;
        default:
          break;
      }
    }
  }

  const handleCancel = (optId?: string, subject?: VideoStreamSubject) => {
    if (onnxTask.status !== TASK_STATUS.IDLE) {
      dispatch(setONNXTask({status: TASK_STATUS.IDLE}));
    } else {
      if (chatConnection) {
        stopTextTask(); // for chats
      }
      if (streamingTask && streamingTask.status !== TASK_STATUS.IDLE) {
        stop(); // for web camera, WebRTC connection
      }
      // for unfinished deployment tasks
      terminateDeploy(optId);
    }
    if (subject) {
      // for any HLS connection
      subject.removeListener('GspTaskStreamObserver');
      subject.raiseStreamFinishEvent();
    }
  };

  const handleSubmit = async (
    deployConfig: DeployConfigState,
    streamSubject: VideoStreamSubject,
    afterTest: Function
  ) => {
    const {
      name,
      description,
      nodeId,
      deploymentId,
      files,
      testSource,
      outputTypes,
      inferenceSource,
      ipCamera,
      videoFile,
      isONNXRuntimeWeb,
      postProcessorParams,
      preProcessorParams,
      useTiling,
      tilingWidth,
      tilingHeight,
      inferenceImageSetId,
      environment,
      analyticsParameters,
    } = deployConfig;
    dispatch(resetAllTasks());

    streamSubject.addListener('GspTaskStreamObserver', new GspTaskStreamObserver());

    try {
      const parameters = [...postProcessorParams, ...preProcessorParams];
      if (parameters.length) {
        await updateParameters({
          projectId,
          applicationId: deploymentId,
          parameters,
        });
      }
    } catch (e) {
      console.error(e);
    }

    if (isONNXRuntimeWeb) {
      const onnxWebTestReport: OnnxTestReport = {
        projectId,
        applicationId: deploymentId,
        name,
        description,
        deploymentType: 'ONNX_WEB_INFERENCE',
        inputSourceType: inferenceSource === '' ? undefined : inferenceSource,
      };
      await postTestOnnxReport(onnxWebTestReport);
      // For now, just set onnx model task status to in progress
      // In the future, we will have BE calls to make
      dispatch(
        setONNXTask({
          status: TASK_STATUS.IN_PROGRESS,
          id: Date.now().toString(),
          params: {
            deploymentId: deployConfig.deploymentId,
            files: deployConfig.files,
            inferenceSource: deployConfig.inferenceSource,
            runtimeMode: deployConfig.runtimeMode,
            videoFile: deployConfig.videoFile,
            ipCamera: deployConfig.ipCamera,
          },
        })
      );
    } else {
      if (
        testSource &&
        testSource !== 'USER_DATA' &&
        testSource !== 'IMAGE_SETS' &&
        testSource !== 'APP_INPUT' &&
        testSource !== 'TEXT_INPUT'
      ) {
        const modelStatistics = outputTypes.includes('modelStatistics');
        const sampleInference = outputTypes.includes('sampleInference');

        const deploymentType =
          modelStatistics && sampleInference
            ? 'VALIDATION_PLUS_INFERENCE'
            : modelStatistics
            ? 'VALIDATION'
            : sampleInference
            ? 'SAMPLE_INFERENCE'
            : 'INFERENCE';

        try {
          const deployRequest: TestInitiateRequest = {
            imageValidationRequest: {
              projectId,
              applicationId: deploymentId,
              name,
              description,
              deploymentType,
              splitType: testSource,
              tilingWidth: useTiling ? tilingWidth : undefined,
              tilingHeight: useTiling ? tilingHeight : undefined,
              environment,
              analyticsParameters,
            },
          };
          await runDeploy(deployRequest);
        } catch (e) {
          dispatch(resetAllTasks());
        }
      } else if (testSource === 'USER_DATA') {
        if (inferenceSource === 'IMAGES') {
          try {
            const deployRequest: TestInitiateRequest = {
              imageInferenceRequest: {
                projectId,
                applicationId: deploymentId,
                name,
                description,
                tilingWidth: useTiling ? tilingWidth : undefined,
                tilingHeight: useTiling ? tilingHeight : undefined,
                environment,
                analyticsParameters,
              },
            };
            if (files.length && deployRequest.imageInferenceRequest) {
              const inferenceData: any = await runInference(
                deploymentId,
                files.map(f => f.file)
              );
              deployRequest.imageInferenceRequest.inferenceDataId = inferenceData.id;
            }
            await runDeploy(deployRequest);
            dispatch(resetInferenceTask());
          } catch (e) {
            dispatch(resetAllTasks());
          }
        } else if (inferenceSource === 'WEB_CAMERA') {
          runStreamingTask({
            projectId,
            applicationId: deploymentId,
            schedulerId: nodeId,
            name,
            description,
            limitedDuration: false,
            inferenceSource,
            environment,
            analyticsParameters,
          });
        } else if (
          ['IP_CAMERA', 'FILE', 'APP_INPUT', 'TEXT_INPUT'].includes(inferenceSource)
        ) {
          runTestInference({
            projectId,
            applicationId: deploymentId,
            schedulerId: nodeId,
            name,
            description,
            inferenceSource: inferenceSource === '' ? undefined : inferenceSource,
            streamingDevices,
            videoFile,
            ipCamera,
            streamSubject,
            environment,
            analyticsParameters,
          });
        }
      } else if (testSource === 'IMAGE_SETS') {
        try {
          const deployRequest: TestInitiateRequest = {
            imageSetsInferenceRequest: {
              projectId,
              applicationId: deploymentId,
              name,
              description,
              tilingWidth: useTiling ? tilingWidth : undefined,
              tilingHeight: useTiling ? tilingHeight : undefined,
              environment,
              analyticsParameters,
              inferenceDataId: inferenceImageSetId,
            },
          };
          await runDeploy(deployRequest);
          dispatch(resetInferenceTask());
        } catch (e) {
          dispatch(resetAllTasks());
        }
      } else if (testSource === 'APP_INPUT') {
        var src = inferenceSource === 'IMAGES' ? 'APP_INPUT' : inferenceSource;
        runTestInference({
          projectId,
          applicationId: deploymentId,
          schedulerId: nodeId,
          name,
          description,
          inferenceSource: src === '' ? undefined : src,
          streamingDevices,
          videoFile,
          ipCamera,
          streamSubject,
          environment,
          analyticsParameters,
        });
      } else if (testSource === 'TEXT_INPUT') {
        runTextTask({
          projectId,
          applicationId: deploymentId,
          schedulerId: nodeId,
          name,
          description,
          signalingServerUrl: serverConfig.videoServerUrl || '',
        });
      }
      afterTest();
    }
  };

  return {handleSubmit, handleCancel};
};
